import { api, endpoints } from '../api/config';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { PDFDocument } from 'pdf-lib';
import { useQuery } from '@tanstack/react-query';
import { Box, Dialog, DialogTitle, DialogContent, IconButton, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, Button, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import { getFutureDateInputProps } from '../utils/dateConstraints';
import {
  COLUMN_MIN_SIZE,
  COLUMN_SIZE,
  DIALOG_CLOSE_BUTTON_STYLE,
  DIALOG_MAX_WIDTH
} from '../constants/styles';

const normalizeLabelSource = (source) => String(source ?? '').trim().toLowerCase();
const isPuLabelSource = (source) => normalizeLabelSource(source) === 'pu';
const isDropshipLabelSource = (source) => normalizeLabelSource(source) === 'dropship';

// Returns true when a picked value indicates a fully-picked order
const isPickedComplete = (pickedValue) => {
  if (pickedValue === null || pickedValue === undefined || pickedValue === '') return false;
  const str = String(pickedValue).trim();
  if (str === '1' || str.toLowerCase() === 'true' || str.toLowerCase() === 'yes' || str.toLowerCase() === 'complete') return true;
  const match = str.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (match) {
    const picked = parseInt(match[1], 10);
    const total = parseInt(match[2], 10);
    return total > 0 && picked >= total;
  }
  return false;
};

// Format a picked-count value for display (e.g. "3/5" → "3", true → "Complete", etc.)
const formatPickedCount = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const str = String(value).trim();
  if (str === '1' || str.toLowerCase() === 'true') return 'Complete';
  if (str === '0' || str.toLowerCase() === 'false') return 'Not Picked';

  // Extract only the X value from X/Y format
  const match = str.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (match) {
    return match[1]; // Return only the X value
  }

  return str;
};

const parseMultiSearchTerms = (value) =>
  String(value || '')
    .split(/[\n,]+/)
    .map((t) => t.trim())
    .filter(Boolean);

const FIXED_MULTILINE_SEARCH_SX = {
  minWidth: 220,
  '& .MuiInputBase-root': {
    fontSize: '0.8125rem',
    height: '32px !important',
    boxSizing: 'border-box',
    alignItems: 'center',
    overflow: 'hidden',
  },
  '& textarea': {
    height: '20px !important',
    maxHeight: '20px !important',
    overflowY: 'auto !important',
    resize: 'none',
    whiteSpace: 'pre',
    lineHeight: '20px',
    py: '0 !important',
  },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};

const SalesOrderListFilterBar = ({
  statusFilter,
  setStatusFilter,
  shipDateFrom,
  shipDateTo,
  trackingNumber,
  orderSearch,
  onApply,
  onViewDetails
}) => {
  const [shipDateFromDraft, setShipDateFromDraft] = useState(shipDateFrom);
  const [shipDateToDraft, setShipDateToDraft] = useState(shipDateTo);
  const [trackingNumberDraft, setTrackingNumberDraft] = useState(trackingNumber);
  const [orderSearchDraft, setOrderSearchDraft] = useState(orderSearch);

  // Sync draft states with external changes (e.g. from local storage on mount)
  useEffect(() => {
    setShipDateFromDraft(shipDateFrom);
  }, [shipDateFrom]);
  useEffect(() => {
    setShipDateToDraft(shipDateTo);
  }, [shipDateTo]);
  useEffect(() => {
    setTrackingNumberDraft(trackingNumber);
  }, [trackingNumber]);
  useEffect(() => {
    setOrderSearchDraft(orderSearch);
  }, [orderSearch]);

  const applyDrafts = () => onApply(shipDateFromDraft, shipDateToDraft, trackingNumberDraft, orderSearchDraft);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="status-filter-label" shrink={!!statusFilter}>Status</InputLabel>
        <Select
          labelId="status-filter-label"
          value={statusFilter}
          label="Status"
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{
            fontSize: '0.8125rem',
            height: '32px',
            '& .MuiSelect-select': { fontSize: '0.8125rem', py: 0.75 }
          }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
        </Select>
      </FormControl>
      <TextField
        label="Order Date From"
        type="date"
        size="small"
        value={shipDateFromDraft}
        onChange={(e) => setShipDateFromDraft(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={getFutureDateInputProps()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonthIcon sx={{ fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: 150,
          '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
        }}
      />
      <TextField
        label="Order Date To"
        type="date"
        size="small"
        value={shipDateToDraft}
        onChange={(e) => setShipDateToDraft(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={getFutureDateInputProps()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonthIcon sx={{ fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: 150,
          '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
        }}
      />
      <TextField
        label="Tracking Number"
        size="small"
        placeholder="Tracking (paste list OK)"
        value={trackingNumberDraft}
        onChange={(e) => setTrackingNumberDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            applyDrafts();
          }
        }}
        multiline
        rows={1}
        sx={FIXED_MULTILINE_SEARCH_SX}
      />
      <TextField
        label="Search SO / PO / Addressee"
        size="small"
        placeholder="SO, PO, or shipping addressee"
        value={orderSearchDraft}
        onChange={(e) => setOrderSearchDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            applyDrafts();
          }
        }}
        multiline
        rows={1}
        sx={{ ...FIXED_MULTILINE_SEARCH_SX, minWidth: 280 }}
      />
      <Button
        variant="contained"
        onClick={applyDrafts}
        disabled={
          (Boolean(shipDateFromDraft) !== Boolean(shipDateToDraft)) ||
          (
            shipDateFromDraft === shipDateFrom &&
            shipDateToDraft === shipDateTo &&
            trackingNumberDraft === trackingNumber &&
            orderSearchDraft === orderSearch
          )
        }
        sx={{
          fontSize: '0.8125rem',
          height: '32px',
          textTransform: 'none',
          px: 2
        }}
      >
        Apply Filters
      </Button>
      <Button
        variant="contained"
        onClick={onViewDetails}
        disabled={statusFilter === 'all'}
        sx={{
          fontSize: '0.8125rem',
          height: '32px',
          textTransform: 'none',
          px: 2
        }}
      >
        View Details
      </Button>
    </Box>
  );
};

const SalesOrderList = () => {
  // Ship date and tracking number filters and drafts
  const [statusFilter, setStatusFilter] = useState('all');
  const [shipDateFrom, setShipDateFrom] = useState('');
  const [shipDateTo, setShipDateTo] = useState('');
  const [trackingNumber, setTrackingNumber] = useState(() => localStorage.getItem('sales-order-list-tracking-number') || '');
  const [orderSearch, setOrderSearch] = useState(() => {
    const saved = localStorage.getItem('sales-order-list-order-search');
    if (saved != null) return saved;
    // Migrate legacy separate SO / PO filters into one search value
    return localStorage.getItem('sales-order-list-transaction-id')
      || localStorage.getItem('sales-order-list-po-number')
      || '';
  });

  // Second-level (detailed) dialog state
  const [detailedDialogOpen, setDetailedDialogOpen] = useState(false);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [detailedData, setDetailedData] = useState([]);

  // View Details dialog state (all open orders line-level data)
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);
  const [viewDetailsError, setViewDetailsError] = useState(null);
  const [viewDetailsData, setViewDetailsData] = useState([]);

  const [selectedLineLevelRows, setSelectedLineLevelRows] = useState(new Set());
  const [selectedDetailedRows, setSelectedDetailedRows] = useState(new Set());
  const [selectedViewDetailsRows, setSelectedViewDetailsRows] = useState(new Set());

  // Line-level dialog state (first-level nested)
  const [lineLevelDialogOpen, setLineLevelDialogOpen] = useState(false);
  const [lineLevelAllowNested, setLineLevelAllowNested] = useState(true);
  const [lineLevelLoading, setLineLevelLoading] = useState(false);
  const [lineLevelError, setLineLevelError] = useState(null);
  const [lineLevelData, setLineLevelData] = useState([]);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [filterError, setFilterError] = useState(null);

  // Column visibility states (persisted)
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('sales-order-list-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [lineLevelColumnVisibility, setLineLevelColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('sales-order-list-linelevel-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [detailedColumnVisibility, setDetailedColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('sales-order-list-detailed-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [viewDetailsColumnVisibility, setViewDetailsColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('sales-order-list-viewdetails-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('sales-order-list-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);
  useEffect(() => {
    localStorage.setItem('sales-order-list-linelevel-column-visibility', JSON.stringify(lineLevelColumnVisibility));
  }, [lineLevelColumnVisibility]);
  useEffect(() => {
    localStorage.setItem('sales-order-list-detailed-column-visibility', JSON.stringify(detailedColumnVisibility));
  }, [detailedColumnVisibility]);
  useEffect(() => {
    localStorage.setItem('sales-order-list-viewdetails-column-visibility', JSON.stringify(viewDetailsColumnVisibility));
  }, [viewDetailsColumnVisibility]);

  const getTrackingNumber = useCallback((row) => {
    const value = row?.tracking_number || row?.trackingNumber || row?.pro_number || row?.proNumber || '';
    return String(value).trim();
  }, []);

  const extractSsccCodesFromRow = useCallback((row) => {
    if (!row || typeof row !== 'object') return [];
    const key = Object.keys(row).find((k) => {
      const normalized = k.toLowerCase();
      return normalized === 'sscc_codes' || normalized === 'sscc_code' || normalized === 'sscc';
    });
    if (!key || row[key] === null || row[key] === undefined) return [];
    const raw = String(row[key]);
    const splitCodes = raw
      .split(/[;,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
    return Array.from(new Set(splitCodes));
  }, []);

  const rowHasLabelIdentifier = useCallback(
    (row) => {
      if (!row || typeof row !== 'object') return false;
      const effectiveSource = selectedSource || row?.source || row?.order_source;
      if (isPuLabelSource(effectiveSource)) {
        return extractSsccCodesFromRow(row).length > 0 || Boolean(getTrackingNumber(row));
      }
      if (isDropshipLabelSource(effectiveSource)) {
        return Boolean(getTrackingNumber(row)) || extractSsccCodesFromRow(row).length > 0;
      }
      return Boolean(getTrackingNumber(row));
    },
    [extractSsccCodesFromRow, getTrackingNumber, selectedSource]
  );

  const getRowSelectionKey = useCallback((row) => {
    const trackingNumber = getTrackingNumber(row);
    const lineId = String(row?.line_id || row?.internal_id || row?.transaction_id || row?.id || '');
    const item = String(row?.item || row?.name || '');
    const fingerprint = JSON.stringify({
      tracking_number: row?.tracking_number ?? row?.trackingNumber ?? row?.pro_number ?? row?.proNumber ?? '',
      line_id: row?.line_id ?? row?.internal_id ?? row?.transaction_id ?? row?.id ?? '',
      item: row?.item ?? row?.name ?? '',
      sscc_codes: row?.sscc_codes ?? row?.sscc_code ?? '',
      location: row?.location ?? row?.location_id ?? '',
      bin_number: row?.bin_number ?? '',
      picked_time: row?.picked_time ?? '',
      picker_name: row?.picker_name ?? '',
      ship_confirm_time: row?.ship_confirm_time ?? '',
      movement: row?.movement ?? '',
      moment_time: row?.moment_time ?? '',
      delivered: row?.delivered ?? '',
      delivery_status: row?.delivery_status ?? '',
      delivery_time: row?.delivery_time ?? '',
      trailer_pickup_date: row?.trailer_pickup_date ?? '',
    });
    return [trackingNumber, lineId, item, fingerprint].filter(Boolean).join('|');
  }, [getTrackingNumber]);

  const updateSelection = useCallback((setter, rowKey, isSelected) => {
    setter((prev) => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(rowKey);
      } else {
        next.delete(rowKey);
      }
      return next;
    });
  }, []);

  const getSelectableRowKeys = useCallback((rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.reduce((acc, row) => {
      if (rowHasLabelIdentifier(row)) {
        acc.push(getRowSelectionKey(row));
      }
      return acc;
    }, []);
  }, [getRowSelectionKey, rowHasLabelIdentifier]);

  const toggleSelectAllRows = useCallback((setter, rows, isSelected) => {
    setter(isSelected ? new Set(getSelectableRowKeys(rows)) : new Set());
  }, [getSelectableRowKeys]);

  const getSelectedTrackingNumbers = useCallback((rows, selectedRows) => {
    if (!Array.isArray(rows) || !selectedRows || selectedRows.size === 0) return [];
    const trackingNumbers = rows.reduce((acc, row) => {
      const rowKey = getRowSelectionKey(row);
      if (selectedRows.has(rowKey)) {
        const trackingNumber = getTrackingNumber(row);
        if (trackingNumber) {
          acc.push(trackingNumber);
        }
      }
      return acc;
    }, []);

    return Array.from(new Set(trackingNumbers));
  }, [getRowSelectionKey, getTrackingNumber]);

  const getSelectedSsccCodes = useCallback((rows, selectedRows) => {
    if (!Array.isArray(rows) || !selectedRows || selectedRows.size === 0) return [];
    const ssccCodes = rows.reduce((acc, row) => {
      const rowKey = getRowSelectionKey(row);
      if (selectedRows.has(rowKey)) {
        acc.push(...extractSsccCodesFromRow(row));
      }
      return acc;
    }, []);
    return Array.from(new Set(ssccCodes));
  }, [extractSsccCodesFromRow, getRowSelectionKey]);

  // FIX 1: refetchOnMount: false — cache valid asel tar re-fetch nako
  const salesOrderListQuery = useQuery({
    queryKey: ['sales-order-list', statusFilter, shipDateFrom, shipDateTo, trackingNumber, orderSearch],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false, // FIX: was true — caused re-fetch on every tab switch/mount
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async () => {
      try {
        const dateParams = {};
        if (shipDateFrom) dateParams.order_date_from = shipDateFrom;
        if (shipDateTo) dateParams.order_date_to = shipDateTo;

        const trackingTerms = parseMultiSearchTerms(trackingNumber);
        const orderTerms = parseMultiSearchTerms(orderSearch);

        const toArray = (response) => {
          const respData = response.data?.data ?? response.data ?? [];
          return Array.isArray(respData) ? respData : respData ? [respData] : [];
        };

        const rowKey = (row) => `${row?.source || ''}|${row?.sonumber || row?.internal_id || ''}`;

        const fetchMerged = async (paramSets) => {
          if (!paramSets.length) {
            const response = await api.get(endpoints.salesOrderList, { params: dateParams });
            return toArray(response);
          }
          const responses = await Promise.all(
            paramSets.map((params) =>
              api.get(endpoints.salesOrderList, { params: { ...dateParams, ...params } })
            )
          );
          const merged = new Map();
          for (const response of responses) {
            for (const row of toArray(response)) {
              const key = rowKey(row);
              if (key !== '|' && !merged.has(key)) merged.set(key, row);
            }
          }
          return Array.from(merged.values());
        };

        // No SO/PO or tracking filters — default list
        if (!trackingTerms.length && !orderTerms.length) {
          return fetchMerged([]);
        }

        let rows = null;

        if (orderTerms.length) {
          rows = await fetchMerged([
            { transaction_id: orderTerms.join(',') },
            ...orderTerms.map((term) => ({ po_number: term })),
            ...orderTerms.map((term) => ({ shipping_addressee: term })),
          ]);
        }

        if (trackingTerms.length) {
          const trackingRows = await fetchMerged(
            trackingTerms.map((term) => ({ tracking_number: term }))
          );
          if (rows) {
            // Both filters set: keep orders that match SO/PO AND tracking
            const trackingKeys = new Set(trackingRows.map(rowKey));
            rows = rows.filter((row) => trackingKeys.has(rowKey(row)));
          } else {
            rows = trackingRows;
          }
        }

        return rows || [];
      } catch (err) {
        console.error('Error fetching sales order list:', err);
        throw new Error(err?.response?.data?.message || err?.message || 'Failed to fetch data');
      }
    }
  });

  const data = salesOrderListQuery.data || [];
  const isLoading = salesOrderListQuery.isLoading;
  const error = salesOrderListQuery.error?.message || null;
  const { refetch } = salesOrderListQuery;

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  const escapeZpl = (value) => String(value ?? '').replaceAll('^', ' ').replaceAll('~', ' ');

  const normalizePackingSlip = (source) => {
    if (!source) return null;

    if (typeof source === 'string') {
      const trimmed = source.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
      try {
        const decoded = JSON.parse(trimmed);
        return normalizePackingSlip(decoded);
      } catch {
        return null;
      }
    }

    if (Array.isArray(source)) {
      if (source.length === 0) return null;
      return normalizePackingSlip(source[0]);
    }

    if (typeof source === 'object') {
      if (source.data && typeof source.data === 'object' && !Array.isArray(source.data)) {
        return source.data;
      }
      return source;
    }

    return null;
  };

  const looksLikeHtml = (bytes) => {
    const sample = new TextDecoder().decode(bytes.slice(0, 100)).trim().toLowerCase();
    return sample.startsWith('<!doctype') || sample.startsWith('<html') || sample.startsWith('<?xml') || sample.startsWith('<body') || sample.startsWith('<!');
  };

  const detectImageType = (bytes) => {
    if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
    if (bytes.length >= 4 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) return 'image/gif';
    if (bytes.length >= 12 && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) return 'image/webp';
    return '';
  };

  const loadImageElement = (bytes, typeHint) => new Promise((resolve, reject) => {
    const blob = new Blob([bytes], { type: typeHint || 'image/png' });
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to decode logo image'));
    };
    img.src = objectUrl;
  });

  const resizeImageOptimized = (image) => {
    const maxWidth = 395;
    const targetHeight = 125;
    const scale = targetHeight / image.height;
    let width = Math.max(1, Math.round(image.width * scale));
    let height = targetHeight;

    if (width > maxWidth) {
      const widthScale = maxWidth / image.width;
      width = maxWidth;
      height = Math.max(1, Math.round(image.height * widthScale));
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0, width, height);
    return canvas;
  };

  const enhanceImageForZpl = (canvas) => {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const px = imageData.data;
    const contrast = 1.55;

    for (let i = 0; i < px.length; i += 4) {
      const gray = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      const contrasted = Math.max(0, Math.min(255, (gray - 128) * contrast + 128));
      const bw = contrasted < 155 ? 0 : 255;
      px[i] = bw;
      px[i + 1] = bw;
      px[i + 2] = bw;
      px[i + 3] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
    return imageData;
  };

  const convertToZplBitmap = (imageData, width, height) => {
    const bytesPerRow = Math.ceil(width / 8);
    const hexRows = [];

    for (let y = 0; y < height; y++) {
      let rowHex = '';
      for (let xByte = 0; xByte < bytesPerRow; xByte++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = xByte * 8 + bit;
          if (x >= width) continue;
          const idx = (y * width + x) * 4;
          const isBlack = imageData.data[idx] < 128;
          if (isBlack) byte |= (1 << (7 - bit));
        }
        rowHex += byte.toString(16).padStart(2, '0').toUpperCase();
      }
      hexRows.push(rowHex);
    }

    const hex = hexRows.join('');
    const chunked = [];
    for (let i = 0; i < hex.length; i += 80) {
      chunked.push(hex.slice(i, i + 80));
    }

    const totalBytes = bytesPerRow * height;
    return {
      totalBytes,
      bytesPerRow,
      hexData: chunked.join('\n')
    };
  };

  const convertBytesToZplImage = async (bytes, typeHint) => {
    const image = await loadImageElement(bytes, typeHint);
    const canvas = resizeImageOptimized(image);
    const imageData = enhanceImageForZpl(canvas);
    const { totalBytes, bytesPerRow, hexData } = convertToZplBitmap(imageData, canvas.width, canvas.height);
    return `^FO35,15\n^GFA,${totalBytes},${totalBytes},${bytesPerRow},${hexData}\n^FS`;
  };

  const fetchImageBytes = async (url, headers = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const resp = await fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal
      });
      const arrayBuffer = await resp.arrayBuffer().catch(() => new ArrayBuffer(0));
      return {
        ok: resp.ok,
        status: resp.status,
        contentType: resp.headers.get('content-type') || '',
        bytes: new Uint8Array(arrayBuffer)
      };
    } catch (error) {
      return {
        ok: false,
        status: 0,
        contentType: '',
        bytes: new Uint8Array(0),
        error: error?.message || 'Failed to fetch image',
        isNetworkError: true
      };
    } finally {
      clearTimeout(timer);
    }
  };

  const normalizeCustomerImageUrl = (rawUrl) => {
    const value = String(rawUrl || '').trim();
    if (!value) return '';

    const forcedPrefix = 'https://4809897.restlets.api.netsuite.com/';
    const coreIndex = value.indexOf('/core');
    if (coreIndex >= 0) {
      return `${forcedPrefix.replace(/\/+$/, '')}${value.slice(coreIndex)}`;
    }

    try {
      const parsed = new URL(value);
      parsed.protocol = 'https:';
      parsed.hostname = '4809897.restlets.api.netsuite.com';
      return parsed.toString();
    } catch {
      return value;
    }
  };

  const buildPackingSlipImageZpl = async (packingSlip) => {
    const customerUrl = normalizeCustomerImageUrl(packingSlip?.customer_url || '');
    if (!customerUrl || !/^https?:\/\//i.test(customerUrl)) return '';

    const referer = (() => {
      try {
        return new URL(customerUrl).origin;
      } catch {
        return undefined;
      }
    })();

    const browserHeaders = {
      'Accept': 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
      ...(referer ? { Referer: referer } : {})
    };

    const imageResp = await fetchImageBytes(customerUrl, browserHeaders);

    if (!imageResp.ok || !imageResp.bytes || imageResp.bytes.length === 0) return '';
    if (looksLikeHtml(imageResp.bytes)) return '';

    const detectedType = detectImageType(imageResp.bytes);
    const contentType = (imageResp.contentType || '').toLowerCase();
    const typeHint = detectedType || (contentType.startsWith('image/') ? contentType : '');
    if (!typeHint) return '';

    try {
      return await convertBytesToZplImage(imageResp.bytes, typeHint);
    } catch {
      return '';
    }
  };

  const cleanShipViaText = (value) => String(value || '')
    .replaceAll('Ã,Â®', '\u00AE')
    .replaceAll('Ã®', '\u00AE')
    .replaceAll('Â®', '\u00AE')
    .replaceAll('Ã', '')
    .replaceAll('Â', '')
    .replace(/\s+/g, ' ')
    .trim();

  const generatePackingSlipZplFromPackingSlip = (packingSlip, logoZpl = '') => {
    const shippingTo = packingSlip?.shipping_to || {};

    const soNumber = String(packingSlip?.transaction_id || '').trim();
    const shipDateRaw = String(packingSlip?.ship_date || '').trim();
    const formattedShipDate = (() => {
      if (!shipDateRaw) {
        const now = new Date();
        return `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}`;
      }
      const parsed = new Date(shipDateRaw);
      if (!isNaN(parsed.getTime())) return `${parsed.getMonth() + 1}/${parsed.getDate()}/${parsed.getFullYear()}`;
      return shipDateRaw;
    })();

    const poNumber = String(packingSlip?.po_number || '').trim();
    const internalId = String(packingSlip?.internal_id || '').trim();
    const packingSlipId = String(packingSlip?.packing_id || '').trim();

    const shipVia = cleanShipViaText(packingSlip?.ship_via || '');
    const poLineCount = poNumber ? Math.ceil(poNumber.length / 22) : 1;
    const orderLineCount = internalId ? Math.ceil(internalId.length / 22) : 0;
    const finalHeaderBottomY = 85 + (poLineCount * 35) + (orderLineCount * 35) + 8;
    const shipViaStartY = Math.max(140, finalHeaderBottomY + 12);
    const shipViaLines = shipVia ? shipVia.split(' ').filter(Boolean) : [];
    const shipViaZpl = shipViaLines
      .map((word, idx) => `^FO430,${shipViaStartY + (idx * 72)}^FB350,1,0,R^FD${escapeZpl(word)}^FS`)
      .join('\n');

    const companyTo = String(shippingTo?.company || '').trim();
    const addressRaw = String(shippingTo?.address || '').trim();
    const addressParts = addressRaw.split(',').map((p) => p.trim()).filter(Boolean);

    let addresseeName = '';
    let address1 = '';
    let address2 = '';
    if (addressParts.length === 1) {
      address1 = addressParts[0];
    } else if (addressParts.length === 2) {
      addresseeName = addressParts[0];
      address1 = addressParts[1];
    } else if (addressParts.length >= 3) {
      addresseeName = addressParts[0];
      address1 = addressParts[1];
      address2 = addressParts.slice(2).join(', ');
    }

    const customerName = addresseeName || companyTo || String(shippingTo?.name || '').trim();
    const addressLine1 = [address1, address2].filter(Boolean).join(', ');
    const city = String(shippingTo?.city || '').trim();
    const state = String(shippingTo?.state || '').trim();
    const zip = String(shippingTo?.zip || '').trim();
    const country = String(shippingTo?.country || 'US').trim() || 'US';
    const cityStateZip = `${city}${city && state ? ' ' : ''}${state}${(city || state) && zip ? ' ' : ''}${zip}`.trim();

    const itemRaw = String(packingSlip?.item || '').trim();
    const isParts = itemRaw.toUpperCase() === 'PARTS';
    const partsDescription = String(
      packingSlip?.description || packingSlip?.Description || packingSlip?.itemDescription || packingSlip?.item_description || ''
    ).trim();
    const itemSku = escapeZpl((isParts ? partsDescription : itemRaw).slice(0, 30));
    const quantity = escapeZpl(String(packingSlip?.quantity || '').trim());

    const packageTextRaw = String(packingSlip?.package_text || '').trim();
    const packageText = escapeZpl(packageTextRaw.replaceAll('\r\n', ' ').replaceAll('\n', ' ').replaceAll('\r', ' '));
    let footerZpl = '^CF0,32\n^FO40,920^FB730,5,6,L^FD^FS';
    if (packageText.length > 0 && packageText.length <= 250) {
      footerZpl = `^CF0,32\n^FO40,920^FB730,5,6,L^FD${packageText}^FS`;
    } else if (packageText.length <= 500) {
      footerZpl = `^CF0,28\n^FO40,920^FB730,7,6,L^FD${packageText}^FS`;
    } else {
      footerZpl = `^CF0,24\n^FO40,920^FB730,10,6,L^FD${packageText}^FS`;
    }

    const orderStartY = 85 + (poLineCount * 35);

    return `^XA
^CI28
^PO0
^FWN
^PW812
^LL1218
^LH0,40

^FX =========================
^FX COMPANY LOGO / NAME (TOP LEFT – BIG TEXT)
^FX =========================
${logoZpl ? `${logoZpl}` : ''}

^FX =========================
^FX TOP RIGHT INFO (RIGHT ALIGNED)
^FX =========================
^CF0,28
^FO430,15^FB350,1,0,R^FD${escapeZpl(soNumber)}^FS
^FO430,50^FB350,1,0,R^FDSHIP DATE: ${escapeZpl(formattedShipDate)}^FS
^FO430,85^FB350,2,5,R^FDPO #: ${escapeZpl(poNumber)}^FS
${internalId ? `^FO430,${orderStartY}^FB350,2,5,R^FDORDER #: ${escapeZpl(internalId)}^FS` : ''}

^FX =========================
^FX SHIP VIA (BIG FONT - RIGHT ALIGNED)
^FX =========================
^CF0,75
${shipViaZpl}

^FX =========================
^FX SHIP TO (LEFT ALIGNED)
^FX =========================
^CF0,34
^FO35,280^FDSHIP TO:^FS
^CF0,30
^FO35,325^FD${escapeZpl(customerName)}^FS
^FO35,362^FD${escapeZpl(addressLine1)}^FS
^FO35,399^FD${escapeZpl(cityStateZip)}^FS
^FO35,436^FD${escapeZpl(country)}^FS

^FX =========================
^FX REF BARCODE (CENTER ALIGNED WITH VALUE UNDER IT)
^FX =========================
^BY3,4,154
^FO165,520^FB912,1,0,C
^BCN,144,Y,N,N^FD${escapeZpl(packingSlipId)}^FS

^FX =========================
^FX ITEM HEADER (CENTER ALIGNED LIKE QUANTITY)
^FX =========================
^FO30,780^GB500,55,3^FS
^FO530,780^GB250,55,3^FS
^CF0,30
^FO40,795^FB480,1,0,C^FDITEM^FS
^FO540,795^FB230,1,0,C^FDQUANTITY^FS

^FX =========================
^FX ITEM ROW (CENTER ALIGNED LIKE QUANTITY)
^FX =========================
^FO30,835^GB500,60,3^FS
^FO530,835^GB250,60,3^FS
^CF0,30
^FO40,855^FB480,1,0,C^FD${itemSku}^FS
^FO540,855^FB230,1,0,C^FD${quantity}^FS

^FX =========================
^FX FOOTER (DYNAMIC SIZE BASED ON TEXT LENGTH)
^FX =========================
${footerZpl}

^XZ`;
  };

  const inferDocumentMimeType = (base64) => {
    const signature = String(base64 || '').trim().slice(0, 20);
    if (signature.startsWith('JVBERi0')) return 'application/pdf';
    if (signature.startsWith('iVBOR')) return 'image/png';
    if (signature.startsWith('/9j/')) return 'image/jpeg';
    if (signature.startsWith('R0lGOD')) return 'image/gif';
    if (signature.startsWith('UklGR')) return 'image/webp';
    return 'application/octet-stream';
  };

  const fileExtensionForMime = (mime) => {
    if (mime === 'application/pdf') return 'pdf';
    if (mime === 'image/png') return 'png';
    if (mime === 'image/jpeg') return 'jpg';
    if (mime === 'image/gif') return 'gif';
    if (mime === 'image/webp') return 'webp';
    return 'bin';
  };

  const createBlobUrlFromBase64 = (base64, mimeType) => {
    try {
      const clean = String(base64 || '').replace(/\s+/g, '');
      const binary = atob(clean);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Failed to decode document base64:', error);
      return null;
    }
  };

  // Map JSON to ZPL helper
  async function mapJsonToZpl(raw, fallbackSsccCode = '') {
    const packingSlip = normalizePackingSlip(raw?.packing_slip ?? raw?.packingSlip);
    if (packingSlip) {
      const logoZpl = await buildPackingSlipImageZpl(packingSlip);
      return generatePackingSlipZplFromPackingSlip(packingSlip, logoZpl);
    }

    const data = raw?.labelData2 ? raw.labelData2 : (raw?.label_data ? raw.label_data : raw || {});
    const from = data.shipping_from || data.ship_from || {};
    const to = data.shipping_to || data.to || {};

    const companyFrom = String(from.company || from.name || from.addressee || '');
    const addressFrom = String(from.address || from.address1 || '');
    const cityFrom = String(from.city || '');
    const stateFrom = String(from.state || '');
    const zipFrom = String(from.zip || from.postal || from.postal_code || '');

    const companyTo = String(to.company || '');
    const addresseeTo = String(to.addressee || '');
    const adress1 = String(to.address || to.address1 || '');
    const adress2 = String(to.address2 || '');
    const cityTo = String(to.city || '');
    const stateTo = String(to.state || '');
    const zipTo = String(to.zip || to.postal || to.postal_code || '');

    const company = companyTo || addresseeTo || '';
    const adress = companyTo ? addresseeTo : '';

    const zipCode = String(data.zip_code || data.zip || zipTo || '');
    const zipDisplay = String(data.zip_display || zipCode);
    const carrierName = String(data.carrier || data.carrier_name || data.ship_via || data.ship_via_name || '').trim();
    const proNumber = String(data.pro_number || data.pro || '');
    const bolNumber = String(data.bol_number || data.bol || '');
    const bolNumberDisplay = String(bolNumber || data.bol_number_display || '');
    const poNumber = String(data.po_number || data.po || '');
    const upc = String(data.upc || data.item || '');
    const qtyDisplay = String(data.quantity ?? data.qty ?? '');
    const cartonInfo = String(data.carton_info || data.carton || '');
    const model = String(data.model || data.description || data.item || '');
    const ssccCode = String(data.sscc_code || data.sscc || raw?.sscc_code || fallbackSsccCode || '');
    const ssccDisplay = String(data.sscc_display || ssccCode);

    const hasAdress2 = adress2.trim().length > 0;
    const addressBlock = hasAdress2
      ? `^FO420,145^FD${adress1}^FS\n^FO420,175^FD${adress2}^FS\n^FO420,205^FD${cityTo}, ${stateTo} ${zipTo}^FS`
      : `^FO420,145^FD${adress1}^FS\n^FO420,175^FD${cityTo}, ${stateTo} ${zipTo}^FS`;

    const isLongCarrier = carrierName.length > 22;
    const carrierZpl = isLongCarrier
      ? `^CF0,28\n^FO470,315^FB280,2,4,L,0^FD${carrierName}^FS`
      : `^CF0,28\n^FO470,315^FD${carrierName}^FS`;
    const proBolFontCmd = isLongCarrier ? '^CF0,22' : '^CF0,24';
    const proY = isLongCarrier ? '385' : '360';
    const bolY = isLongCarrier ? '415' : '390';

    const template = `^XA
^PW772
^LL1178
^CI28
^CF0,38
^FO20,30^FDSHIP FROM:^FS
^FO420,30^FDSHIP TO:^FS
^CF0,28
^FO20,100^FD$companyFrom^FS
^FO20,135^FD$addressFrom^FS
^FO20,170^FD$cityFrom, $stateFrom, $zipFrom^FS
^FO420,85^FD$company^FS
^FO420,115^FD$adress^FS
$addressBlock
^FO390,20^GB2,440,7^FS
^FO15,265^GB740,7,7^FS
^BY2,2,95
^FO55,315^BCN,95,N,N,N^FD$zipCode^FS
^CF0,26
^FO30,420^FB320,1,0,C,0^FD$zipDisplay^FS
$carrierZpl
$proBolFontCmd
^FO470,$proY^FDPro#: $proNumber^FS
^FO470,$bolY^FDBOL#: $bolNumberDisplay^FS
^FO15,460^GB740,7,7^FS
^CF0,28
^FO20,490^FDPO# $poNumber^FS
^BY2,2,85
^FO420,510^BCN,85,N,N,N^FD$poNumber^FS
^CF0,28
^FO20,635^FDUPC: $upc / SINGLE ASIN^FS
^FO20,675^FDQTY: $qtyDisplay^FS
^CF0,28
^FO20,715^FDCARTON:^FS
^FO140,715^FD$cartonInfo^FS
^CF0,28
^FO20,755^FDMODEL: $model^FS
^FO15,795^GB740,7,7^FS
^CF0,30
^FO8,825^FB772,1,0,C,0^FDSSCC - 18^FS
^BY2.5,3,200
^FO2.5,885^BCN,200,N,N,N^FD$ssccCode^FS
^CF0,38
^FO0,1120^FB772,1,0,C,0^FD$ssccDisplay^FS
^XZ`;

    const filled = template
      .replaceAll('$companyFrom', companyFrom)
      .replaceAll('$addressFrom', addressFrom)
      .replaceAll('$cityFrom', cityFrom)
      .replaceAll('$stateFrom', stateFrom)
      .replaceAll('$zipFrom', zipFrom)
      .replaceAll('$company', company)
      .replaceAll('$adress', adress)
      .replaceAll('$addressBlock', addressBlock)
      .replaceAll('$zipCode', zipCode)
      .replaceAll('$zipDisplay', zipDisplay)
      .replaceAll('$carrierZpl', carrierZpl)
      .replaceAll('$proBolFontCmd', proBolFontCmd)
      .replaceAll('$proY', String(proY))
      .replaceAll('$bolY', String(bolY))
      .replaceAll('$proNumber', proNumber)
      .replaceAll('$bolNumberDisplay', bolNumberDisplay)
      .replaceAll('$poNumber', poNumber)
      .replaceAll('$upc', upc)
      .replaceAll('$qtyDisplay', qtyDisplay)
      .replaceAll('$cartonInfo', cartonInfo)
      .replaceAll('$model', model)
      .replaceAll('$ssccCode', ssccCode)
      .replaceAll('$ssccDisplay', ssccDisplay);

    return filled;
  }

  async function buildDropshipLabelPreviewItem(trackingNumber) {
    const response = await api.get('/admin-dropship-reprint-label', { params: { tracking_number: trackingNumber } });
    const payload = response.data || {};

    // added console.log for debugging
    console.log('Dropship payload:', JSON.stringify(payload, null, 2));

    const itemWarnings = [];

    const documents = (Array.isArray(payload.documents) ? payload.documents : [])
      .map((doc, index) => {
        const mediaBase64 = String(doc?.media || '').trim();
        if (!mediaBase64) return null;
        const mimeType = inferDocumentMimeType(mediaBase64);
        const url = createBlobUrlFromBase64(mediaBase64, mimeType);
        if (!url) return null;
        return {
          url,
          mimeType,
          name: `document_${index + 1}.${fileExtensionForMime(mimeType)}`
        };
      })
      .filter(Boolean);

    let labelImageUrl = null;
    const zplBase64 = payload.zpl_label;
    if (zplBase64) {
      try {
        const zpl = atob(zplBase64);
        const labelaryResp = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
          method: 'POST',
          headers: { 'Accept': 'image/png', 'Content-Type': 'application/x-www-form-urlencoded' },
          body: zpl
        });

        if (labelaryResp.ok) {
          const blob = await labelaryResp.blob();
          labelImageUrl = URL.createObjectURL(blob);
        } else {
          itemWarnings.push('Failed to convert original label to image');
        }
      } catch (error) {
        console.error('Error converting original ZPL:', error);
        itemWarnings.push(error?.message || 'Failed to convert original label');
      }
    }

    let customLabelImageUrl = null;
    let customLabelSource = null;
    try {
      const zplTemplate = await mapJsonToZpl(payload, '');
      if (zplTemplate) {
        const mappedResp = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
          method: 'POST',
          headers: { 'Accept': 'image/png', 'Content-Type': 'application/x-www-form-urlencoded' },
          body: zplTemplate
        });

        if (mappedResp.ok) {
          const mappedBlob = await mappedResp.blob();
          customLabelImageUrl = URL.createObjectURL(mappedBlob);
          customLabelSource = payload?.labelData2 ? 'labelData2' : (payload?.packing_slip ? 'packing_slip' : 'mapped');
        } else {
          itemWarnings.push('Failed to convert mapped label to image');
        }
      }
    } catch (error) {
      console.error('Error creating mapped label:', error);
      itemWarnings.push(error?.message || 'Failed to create mapped label');
    }

    return {
      mode: 'dropship',
      trackingNumber,
      labelImageUrl,
      customLabelImageUrl,
      customLabelSource,
      documents,
      warnings: itemWarnings
    };
  }

  const mapLtlJsonToZpl = (raw, fallbackSsccCode = '') => {
    const payload = raw?.label_data ? raw.label_data : raw || {};
    const from = payload.shipping_from || {};
    const to = payload.shipping_to || {};

    const customerName = String(payload.customer_name || payload.customer || '').trim();
    const isAmazonCustomer = customerName.toUpperCase() === 'AMAZON-1YZ7N';

    const nameFrom = String(from.name1 || from.name || from.company || '').trim();
    const secondaryFrom = String(from.name2 || '').trim();
    const addressFrom = String(from.address || '');
    const cityFrom = String(from.city || '');
    const stateFrom = String(from.state || '');
    const zipFrom = String(from.zip || '');

    const companyTo = String(to.company || '');
    const addressTo = String(to.address || '');
    const cityTo = String(to.city || '');
    const stateTo = String(to.state || '');
    const zipTo = String(to.zip || '');

    const company = companyTo.split(',')[0].trim();
    const adress = companyTo.includes(',')
      ? companyTo.split(',').slice(1).join(',').trim()
      : '';

    let adress1 = '';
    let adress2 = '';
    if (addressTo.includes('&')) {
      const parts = addressTo.split('&');
      adress1 = parts[0].trim();
      if (parts.length > 1) {
        adress2 = parts[1].trim();
      }
    } else {
      adress1 = addressTo;
    }

    const city = cityTo;
    let poNumber = String(payload.po_number || '');
    if (poNumber.endsWith('-SC')) {
      poNumber = poNumber.substring(0, poNumber.length - 3);
    } else if (poNumber.endsWith('-NJ')) {
      poNumber = poNumber.substring(0, poNumber.length - 3);
    }

    const model = String(payload.model || '');
    const upc = String(payload.upc || '');
    const cartonInfo = String(payload.carton_info || '');
    const bolNumberDisplay = String(payload.bol_number || '').replaceAll('-', '');
    const ssccCode = String(payload.sscc_code || raw?.sscc_code || fallbackSsccCode || '');
    const ssccDisplay = String(payload.sscc_display || ssccCode);
    const zipCode = String(payload.zip_code || '');
    const zipDisplay = String(payload.zip_display || zipCode);
    const proNumber = String(payload.pro_number || '');
    const carrierName = String(payload.carrier_name || payload.carrier || '').trim();
    const qtyDisplay = '1';

    const hasAdress2 = adress2.trim().length > 0;
    const addressBlock = hasAdress2
      ? `^FO420,165^FD${adress1}^FS\n^FO420,195^FD${adress2}^FS\n^FO420,225^FD${city}, ${stateTo} ${zipTo}^FS`
      : `^FO420,165^FD${adress1}^FS\n^FO420,195^FD${city}, ${stateTo} ${zipTo}^FS`;

    const carrierLabel = isAmazonCustomer ? carrierName : 'CARRIER';
    const isLongCarrier = carrierLabel.trim().length > 22;
    const carrierZpl = isLongCarrier
      ? `^CF0,28\n^FO420,315^FB280,2,4,L,0^FD${carrierLabel}^FS`
      : `^CF0,28\n^FO420,315^FD${carrierLabel}^FS`;
    const proBolFontCmd = isLongCarrier ? '^CF0,22' : '^CF0,24';
    const proY = isLongCarrier ? '385' : '360';
    const bolY = isLongCarrier ? '415' : '390';

    const bolLine = isAmazonCustomer
      ? `^FO420,${bolY}^FDBOL#: ${bolNumberDisplay}^FS`
      : `^FO420,${bolY}^FDBOL#:^FS`;

    const upcLine = isAmazonCustomer
      ? `^FO20,635^FDUPC: ${upc} / SINGLE ASIN^FS`
      : `^FO20,635^FDUPC: ${upc}^FS`;

    const template = `^XA
^PW772
^LL1178
^CI28
^CF0,38
^FO20,30^FDSHIP FROM:^FS
^FO420,30^FDSHIP TO:^FS
^CF0,28
^FO20,100^FD$nameFrom^FS
^FO20,135^FD$secondaryFrom^FS
^FO20,170^FD$addressFrom^FS
^FO20,205^FD$cityFrom, $stateFrom, $zipFrom^FS
^FO420,95^FD$company^FS
^FO420,130^FD$adress^FS
$addressBlock
^FO390,20^GB2,440,7^FS
^FO15,265^GB740,7,7^FS
^BY2,2,95
^FO55,315^BCN,95,N,N,N^FD$zipCode^FS
^CF0,26
^FO30,420^FB320,1,0,C,0^FD$zipDisplay^FS
$carrierZpl
$proBolFontCmd
^FO420,$proY^FDPro#: $proNumber^FS
$bolLine
^FO15,460^GB740,7,7^FS
^CF0,28
^FO20,490^FDPO# $poNumber^FS
^BY2,2,85
^FO420,510^BCN,85,N,N,N^FD$poNumber^FS
^CF0,28
$upcLine
^FO20,675^FDQTY: $qtyDisplay^FS
^CF0,28
^FO20,715^FDCARTON:^FS
^FO140,715^FD$cartonInfo^FS
^CF0,28
^FO20,755^FDMODEL: $model^FS
^FO15,795^GB740,7,7^FS
^CF0,30
^FO8,825^FB772,1,0,C,0^FDSSCC - 18^FS
^BY2.5,3,200
^FO2.5,885^BCN,200,N,N,N^FD$ssccCode^FS
^CF0,38
^FO0,1120^FB772,1,0,C,0^FD$ssccDisplay^FS
^XZ`;

    return template
      .replaceAll('$nameFrom', nameFrom)
      .replaceAll('$secondaryFrom', secondaryFrom)
      .replaceAll('$addressFrom', addressFrom)
      .replaceAll('$cityFrom', cityFrom)
      .replaceAll('$stateFrom', stateFrom)
      .replaceAll('$zipFrom', zipFrom)
      .replaceAll('$company', company)
      .replaceAll('$adress', adress)
      .replaceAll('$addressBlock', addressBlock)
      .replaceAll('$zipCode', zipCode)
      .replaceAll('$zipDisplay', zipDisplay)
      .replaceAll('$carrierZpl', carrierZpl)
      .replaceAll('$proBolFontCmd', proBolFontCmd)
      .replaceAll('$proY', String(proY))
      .replaceAll('$bolY', String(bolY))
      .replaceAll('$proNumber', proNumber)
      .replaceAll('$bolLine', bolLine)
      .replaceAll('$upcLine', upcLine)
      .replaceAll('$poNumber', poNumber)
      .replaceAll('$qtyDisplay', qtyDisplay)
      .replaceAll('$cartonInfo', cartonInfo)
      .replaceAll('$model', model)
      .replaceAll('$ssccCode', ssccCode)
      .replaceAll('$ssccDisplay', ssccDisplay);
  };

  const convertLtlZplToImage = async (zpl) => {
    const response = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
      method: 'POST',
      headers: {
        Accept: 'image/png',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: zpl
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to convert ZPL to image: ${errorText}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  };

  const generateLtlPdfWithRetry = async (zpl, maxRetries = 3, delay = 500) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
          method: 'POST',
          headers: {
            Accept: 'application/pdf',
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: zpl
        });
        if (!response.ok) {
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, delay * attempt));
            continue;
          }
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        return await response.arrayBuffer();
      } catch (error) {
        if (attempt === maxRetries) throw error;
        await new Promise((resolve) => setTimeout(resolve, delay * attempt));
      }
    }
    throw new Error('Failed to generate PDF');
  };

  async function buildLtlLabelPreviewItem(ssccCode) {
    const response = await api.get(`/item-reprint-label?sscc_code=${encodeURIComponent(ssccCode)}`);
    const labelData = response.data || {};
    const zpl = mapLtlJsonToZpl(labelData, ssccCode);
    const imageUrl = await convertLtlZplToImage(zpl);
    return { mode: 'pu', ssccCode, imageUrl, zpl };
  }

  // Process the data - filter based on status filter
  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    if (statusFilter === 'all') {
      return data;
    }

    const normalizedStatus = statusFilter === 'closed' ? 'closed' : 'open';

    return data.filter(item => {
      const pickedValue = item.picked || item.is_picked || item.ispicked;
      const isComplete = isPickedComplete(pickedValue);

      if (normalizedStatus === 'closed') {
        return isComplete;
      }

      return !isComplete;
    });
  }, [data, statusFilter]);

  useEffect(() => {
    setSelectedLineLevelRows(new Set());
  }, [lineLevelData]);

  useEffect(() => {
    setSelectedDetailedRows(new Set());
  }, [detailedData]);

  useEffect(() => {
    setSelectedViewDetailsRows(new Set());
  }, [viewDetailsData]);

  const openLineLevelDialogForTransaction = async (transactionId, source, mode = 'nested', initialPo = '') => {
    if (!transactionId) return;
    setSelectedTransactionId(transactionId);
    setSelectedSource(source || null);

    setDetailedDialogOpen(true);
    setDetailedLoading(true);
    setLineLevelDialogOpen(false);
    setLineLevelLoading(false);
    setLineLevelError(null);
    setLineLevelData([]);
    setDetailedData([]);
    setSelectedDetailedRows(new Set());
    setSelectedLineLevelRows(new Set());

    try {
      const params = { transaction_id: transactionId };
      if (source) params.source = source;
      const response = await api.get(endpoints.salesOrderLineLevel, { params });
      const responseData = response.data?.data ?? response.data ?? [];
      const dataArray = Array.isArray(responseData) ? responseData : (responseData ? [responseData] : []);

      const enrichedDataArray = dataArray.map((item) => {
        const itemPo = item?.po ?? item?.po_number ?? item?.ponumber ?? initialPo;
        return {
          ...item,
          po: itemPo,
          po_number: itemPo,
        };
      });

      setLineLevelData(enrichedDataArray);

      const nestedRows = enrichedDataArray.flatMap((item) => {
        const itemPo = item?.po ?? item?.po_number ?? item?.ponumber ?? initialPo;
        if (Array.isArray(item?.data) && item.data.length > 0) {
          return item.data.map((d) => ({
            ...d,
            po: d?.po ?? d?.po_number ?? d?.ponumber ?? itemPo,
            po_number: d?.po_number ?? d?.po ?? d?.ponumber ?? itemPo,
          }));
        }
        return [item];
      });
      const detailedItems = nestedRows.length > 0 ? nestedRows : enrichedDataArray;

      setDetailedData(detailedItems);
    } catch (err) {
      console.error('Error fetching line-level data:', err);
      setLineLevelError(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to load line-level data');
      setDetailedData([]);
    } finally {
      setDetailedLoading(false);
    }
  };

  const openLineLevelDialogForRow = useCallback((row, mode = 'nested') => {
    if (!row) return;
    const transactionId = row?.sonumber || row?.transaction_id;
    if (!transactionId) {
      console.error('No sonumber/transaction_id found in row:', row);
      return;
    }
    const source = row?.source || row?.order_source || row?.label_source;
    const initialPo = row?.po ?? row?.po_number ?? row?.ponumber ?? '';
    void openLineLevelDialogForTransaction(transactionId, source, mode, initialPo);
  }, []);

  // Generate columns dynamically from the data
  const columns = useMemo(() => {
    if (!processedData || processedData.length === 0) {
      return [];
    }

    const allKeysSet = new Set();
    const sampleSize = Math.min(processedData.length, 10);
    for (let i = 0; i < sampleSize; i += 1) {
      Object.keys(processedData[i] || {}).forEach(k => allKeysSet.add(k));
    }
    const keys = Array.from(allKeysSet);

    const primaryOrder = [
      'sonumber',
      'internal_id',
      'customer_name',
      'po',
      'po_number',
      'ponumber',
      'customer_order',
      'status',
      'location',
      'ship_date',
      'ship_via',
      'carrier',
      'priority',
      'source',
      'total_qty',
      'ready_for_pick',
      'picked',
      'ship_confirmed',
      'movement',
      'moment_time',
      'date_time_created',
      'name',
      'item',
      'quantity',
      'transaction_id',
    ];

    const primaries = primaryOrder.filter(k => keys.includes(k));
    const rest = keys.filter(k => !primaryOrder.includes(k) && !['created_at', 'updated_at'].includes(k));
    const ordered = [...primaries, ...rest];

    const generatedColumns = ordered.map(key => {
      const shouldFormatPicked = (key === 'picked' || key === 'is_picked' || key === 'ready_for_picking' || key === 'ready_for_pick' || key === 'ship_confirmed');
      return {
        accessorKey: key,
        header: key
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
        cell: info => {
          const value = info.getValue();
          const normalizedValue = shouldFormatPicked ? formatPickedCount(value) : value;
          if (typeof normalizedValue === 'number') {
            return Math.round(normalizedValue);
          }
          if (typeof normalizedValue === 'object' && normalizedValue !== null) {
            return JSON.stringify(normalizedValue);
          }
          return normalizedValue || '-';
        }
      };
    });

    return generatedColumns;
  }, [processedData]);

  const getRowId = useCallback((row, index) => {
    if (!row) return `row_${index ?? 0}`;
    const primaryId = String(row.line_id || row.id || row.internal_id || row.sonumber || row.transaction_id || '');
    const item = String(row.item || '');
    const binNumber = String(row.bin_number || '');
    const quantity = String(row.quantity || '');
    const trackingNumber = String(row.tracking_number || row.trackingNumber || '');

    const uniqueKey = [
      primaryId,
      item,
      binNumber,
      quantity,
      trackingNumber,
      index !== undefined && index !== null ? index : ''
    ].filter(Boolean).join('|');

    return `row_${uniqueKey}`;
  }, []);

  const exportValueTransform = useCallback((val, row, columnId) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE';

    let processedVal = val;
    const shouldFormat = columnId && (columnId === 'picked' || columnId === 'is_picked' || columnId === 'ready_for_picking' || columnId === 'ready_for_pick' || columnId === 'ship_confirmed');
    if (shouldFormat) {
      processedVal = formatPickedCount(val);
    }

    if (Array.isArray(processedVal)) return processedVal.map(v => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join('; ');
    if (typeof processedVal === 'object') return JSON.stringify(processedVal);
    return String(processedVal);
  }, []);

  const handleRowClick = useCallback((row) => {
    openLineLevelDialogForRow(row, 'nested');
  }, []);

  const handleLineLevelRowClick = useCallback((row) => {
    if (!row || !row.data || !Array.isArray(row.data)) {
      return;
    }

    setDetailedLoading(true);
    setDetailedDialogOpen(true);
    const rowPo = row?.po ?? row?.po_number ?? row?.ponumber ?? '';
    const mappedDetailed = row.data.map((d) => ({
      ...d,
      po: d?.po ?? d?.po_number ?? d?.ponumber ?? rowPo,
      po_number: d?.po_number ?? d?.po ?? d?.ponumber ?? rowPo,
    }));
    setDetailedData(mappedDetailed);
    setDetailedLoading(false);
  }, []);

  // FIX 2: handleViewDetails — already fetched data use kara, no duplicate API call
  const handleViewDetails = useCallback(async () => {
    setViewDetailsDialogOpen(true);
    setViewDetailsLoading(true);
    setViewDetailsError(null);
    setViewDetailsData([]);

    try {
      // Use already-fetched data from salesOrderListQuery — no extra API call needed!
      let dataArray = Array.isArray(data) ? [...data] : [];

      if (statusFilter && statusFilter !== 'all') {
        const normalizedStatus = statusFilter === 'closed' ? 'closed' : 'open';
        dataArray = dataArray.filter((row) => {
          const pickedValue = row?.picked ?? row?.is_picked ?? row?.ispicked;
          const isComplete = isPickedComplete(pickedValue);
          return normalizedStatus === 'closed' ? isComplete : !isComplete;
        });
      }

      setViewDetailsData(dataArray);
    } catch (err) {
      console.error('Error processing view details data:', err);
      setViewDetailsError(err?.message || 'Failed to load data');
      setViewDetailsData([]);
    } finally {
      setViewDetailsLoading(false);
    }
  }, [statusFilter, data]);

  const handleApplyFilters = useCallback((newShipDateFrom, newShipDateTo, newTrackingNumber, newOrderSearch) => {
    const hasFrom = Boolean(newShipDateFrom);
    const hasTo = Boolean(newShipDateTo);
    if (hasFrom !== hasTo) {
      setFilterError('Order date from and order date to must both be set together.');
      return;
    }
    setFilterError(null);
    setShipDateFrom(newShipDateFrom);
    setShipDateTo(newShipDateTo);
    setTrackingNumber(newTrackingNumber);
    setOrderSearch(newOrderSearch);

    localStorage.setItem('sales-order-list-tracking-number', newTrackingNumber || '');
    localStorage.setItem('sales-order-list-order-search', newOrderSearch || '');
    localStorage.removeItem('sales-order-list-transaction-id');
    localStorage.removeItem('sales-order-list-po-number');
  }, []);

  // State for label preview dialog
  const [labelPreviewItems, setLabelPreviewItems] = useState([]);
  const [labelLoading, setLabelLoading] = useState(false);
  const [labelError, setLabelError] = useState(null);
  const [labelPreviewOpen, setLabelPreviewOpen] = useState(false);
  const [labelPreviewMode, setLabelPreviewMode] = useState('dropship');
  const [allZplLabels, setAllZplLabels] = useState([]);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const clearLabelPreviewItems = useCallback(() => {
    setLabelPreviewItems((prev) => {
      prev.forEach((item) => {
        if (item?.labelImageUrl) URL.revokeObjectURL(item.labelImageUrl);
        if (item?.customLabelImageUrl) URL.revokeObjectURL(item.customLabelImageUrl);
        if (item?.imageUrl) URL.revokeObjectURL(item.imageUrl);
        (item?.documents || []).forEach((doc) => {
          if (doc?.url) URL.revokeObjectURL(doc.url);
        });
      });
      return [];
    });
    setAllZplLabels([]);
  }, []);

  const closeLabelPreviewDialog = useCallback(() => {
    setLabelPreviewOpen(false);
    clearLabelPreviewItems();
    setLabelError(null);
    setPdfDownloading(false);
  }, [clearLabelPreviewItems]);

  const downloadCombinedLtlPdf = useCallback(async (zplArray, filename = 'labels.pdf') => {
    try {
      if (!zplArray || zplArray.length === 0) {
        setLabelError('No labels to download');
        return;
      }
      setPdfDownloading(true);
      setLabelError(null);
      const mergedPdf = await PDFDocument.create();
      let successCount = 0;
      let failedCount = 0;
      for (let i = 0; i < zplArray.length; i += 1) {
        try {
          const pdfBytes = await generateLtlPdfWithRetry(zplArray[i]);
          const pdfDoc = await PDFDocument.load(pdfBytes);
          const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
          pages.forEach((page) => mergedPdf.addPage(page));
          successCount += 1;
          if (i < zplArray.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        } catch {
          failedCount += 1;
        }
      }
      if (mergedPdf.getPageCount() === 0) {
        throw new Error('No labels could be generated');
      }
      if (failedCount > 0) {
        setLabelError(`Generated ${successCount} of ${zplArray.length} labels. ${failedCount} label(s) failed.`);
      }
      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setLabelError(err?.message || 'Failed to download PDF');
    } finally {
      setPdfDownloading(false);
    }
  }, []);

  // FIX 3: Parallel label fetch with Promise.allSettled — sequential nako
  const handleGenerateDropshipLabels = useCallback(async (trackingNumbers) => {
    const uniqueTrackingNumbers = Array.from(new Set((trackingNumbers || []).filter(Boolean)));
    if (uniqueTrackingNumbers.length === 0) {
      setLabelError('Please select at least one row with a tracking number.');
      return;
    }

    setLabelPreviewMode('dropship');
    setLabelLoading(true);
    setLabelError(null);
    clearLabelPreviewItems();
    setLabelPreviewOpen(true);

    const previewItems = [];
    const warnings = [];

    try {
      // FIX: Parallel fetch instead of sequential — much faster for multiple labels
      const results = await Promise.allSettled(
        uniqueTrackingNumbers.map(tn => buildDropshipLabelPreviewItem(tn))
      );

      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          const item = result.value;
          if (
            item.labelImageUrl ||
            item.customLabelImageUrl ||
            (item.documents && item.documents.length > 0)
          ) {
            previewItems.push(item);
          } else {
            warnings.push(`${uniqueTrackingNumbers[i]}: No label preview available.`);
          }
          if (item.warnings?.length) {
            warnings.push(...item.warnings.map((message) => `${uniqueTrackingNumbers[i]}: ${message}`));
          }
        } else {
          const error = result.reason;
          warnings.push(`${uniqueTrackingNumbers[i]}: ${error?.response?.data?.message || error?.message || 'Failed to fetch label data'}`);
        }
      });

      setLabelPreviewItems(previewItems);
      if (warnings.length > 0) {
        setLabelError(warnings.join('\n'));
      } else if (previewItems.length === 0) {
        setLabelError('No labels could be generated.');
      }
    } finally {
      setLabelLoading(false);
    }
  }, [clearLabelPreviewItems]);

  // FIX 3 (same): PU labels also parallel
  const handleGeneratePuLabels = useCallback(async (ssccCodes) => {
    const uniqueCodes = Array.from(new Set((ssccCodes || []).filter(Boolean)));
    if (uniqueCodes.length === 0) {
      setLabelError('Please select at least one row with an SSCC code.');
      return;
    }

    setLabelPreviewMode('pu');
    setLabelLoading(true);
    setLabelError(null);
    clearLabelPreviewItems();
    setLabelPreviewOpen(true);

    const previewItems = [];
    const warnings = [];
    const zplLabels = [];

    try {
      // FIX: Parallel fetch instead of sequential
      const results = await Promise.allSettled(
        uniqueCodes.map(ssccCode => buildLtlLabelPreviewItem(ssccCode))
      );

      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          const item = result.value;
          previewItems.push(item);
          if (item.zpl) zplLabels.push(item.zpl);
        } else {
          const error = result.reason;
          const status = error?.response?.status;
          const message =
            status === 404
              ? 'Item is not picked'
              : (error?.response?.data?.message || error?.message || 'Failed to fetch label data');
          warnings.push(`${uniqueCodes[i]}: ${message}`);
        }
      });

      setLabelPreviewItems(previewItems);
      setAllZplLabels(zplLabels);
      if (warnings.length > 0) {
        setLabelError(warnings.join('\n'));
      } else if (previewItems.length === 0) {
        setLabelError('No labels could be generated.');
      }
    } finally {
      setLabelLoading(false);
    }
  }, [clearLabelPreviewItems]);

  const handleGenerateLabel = useCallback(async (row) => {
    if (!row) return;
    if (isPuLabelSource(selectedSource)) {
      const ssccCodes = extractSsccCodesFromRow(row);
      if (ssccCodes.length === 0) {
        setLabelError('No SSCC code found for this row.');
        return;
      }
      return handleGeneratePuLabels([ssccCodes[0]]);
    }
    if (isDropshipLabelSource(selectedSource)) {
      const trackingNumber = getTrackingNumber(row);
      if (!trackingNumber) {
        setLabelError('No tracking number found for this row.');
        return;
      }
      return handleGenerateDropshipLabels([trackingNumber]);
    }
    setLabelError(
      selectedSource
        ? `Unsupported source for label generation: ${selectedSource}`
        : 'Order source is missing. Cannot determine which label API to use.'
    );
  }, [
    extractSsccCodesFromRow,
    getTrackingNumber,
    handleGenerateDropshipLabels,
    handleGeneratePuLabels,
    selectedSource,
  ]);

  const handleGenerateSelectedLabels = useCallback((rows, selectedRows) => {
    if (isPuLabelSource(selectedSource)) {
      const ssccCodes = getSelectedSsccCodes(rows, selectedRows);
      return handleGeneratePuLabels(ssccCodes);
    }
    if (isDropshipLabelSource(selectedSource)) {
      const trackingNumbers = getSelectedTrackingNumbers(rows, selectedRows);
      return handleGenerateDropshipLabels(trackingNumbers);
    }
    setLabelError(
      selectedSource
        ? `Unsupported source for label generation: ${selectedSource}`
        : 'Order source is missing. Cannot determine which label API to use.'
    );
  }, [
    getSelectedSsccCodes,
    getSelectedTrackingNumbers,
    handleGenerateDropshipLabels,
    handleGeneratePuLabels,
    selectedSource,
  ]);

  const lineLevelSelectableRowKeys = useMemo(() => getSelectableRowKeys(lineLevelData), [lineLevelData, getSelectableRowKeys]);
  const detailedSelectableRowKeys = useMemo(() => getSelectableRowKeys(detailedData), [detailedData, getSelectableRowKeys]);
  const viewDetailsSelectableRowKeys = useMemo(() => getSelectableRowKeys(viewDetailsData), [viewDetailsData, getSelectableRowKeys]);

  const createSelectionColumn = useCallback((selectedRows, setSelectedRows) => ({
    accessorKey: '__select_labels',
    header: '\u200B',
    minSize: 60,
    size: 70,
    forceFirst: true,
    cell: (info) => {
      const row = info.row?.original || info.getValue();
      if (!rowHasLabelIdentifier(row)) return '-';

      const rowKey = getRowSelectionKey(row);
      return (
        <Box
          data-checkbox-cell="true"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
        >
          <Checkbox
            checked={selectedRows.has(rowKey)}
            onChange={(e) => {
              e.stopPropagation();
              updateSelection(setSelectedRows, rowKey, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
        </Box>
      );
    }
  }), [getRowSelectionKey, rowHasLabelIdentifier, updateSelection]);

  const buildLineLevelColumns = useCallback((selectedRows, setSelectedRows) => [
    createSelectionColumn(selectedRows, setSelectedRows),
    {
      accessorKey: 'generate_label',
      header: 'Generate Label',
      minSize: 120,
      size: 140,
      cell: info => {
        const row = info.row?.original || info.getValue();
        return rowHasLabelIdentifier(row) ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleGenerateLabel(row)}
            disabled={labelLoading}
          >
            Generate Label
          </Button>
        ) : '-';
      }
    },
    {
      accessorKey: 'line_id',
      header: 'Line ID',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'po_number',
      header: 'PO Number',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'item',
      header: 'Item',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'quantity',
      header: 'Quantity',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        return value !== null && value !== undefined ? value : '-';
      }
    },
    {
      accessorKey: 'location',
      header: 'Location',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'ready_for_picking',
      header: 'Ready For Picking',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        const normalizedValue = formatPickedCount(value);
        return normalizedValue || '-';
      }
    },
    {
      accessorKey: 'picked',
      header: 'Picked',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        const normalizedValue = formatPickedCount(value);
        return normalizedValue || '-';
      }
    },
    {
      accessorKey: 'ship_confirmed',
      header: 'Ship Confirmed',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        const normalizedValue = formatPickedCount(value);
        return normalizedValue || '-';
      }
    }
  ], [createSelectionColumn, handleGenerateLabel, labelLoading, rowHasLabelIdentifier]);

  const reasonColumn = useMemo(() => ({
    accessorKey: 'reason',
    header: 'Reason',
    minSize: COLUMN_MIN_SIZE,
    size: COLUMN_SIZE,
    cell: info => info.getValue() || '-'
  }), []);

  const lineLevelColumns = useMemo(() => {
    const cols = buildLineLevelColumns(selectedLineLevelRows, setSelectedLineLevelRows);
    const hasReason = lineLevelData.some(row => row?.reason != null && row?.reason !== '');
    if (hasReason) {
      cols.push(reasonColumn);
    }
    return cols;
  }, [buildLineLevelColumns, selectedLineLevelRows, lineLevelData, reasonColumn]);

  const viewDetailsColumns = useMemo(() => {
    const fullColumns = buildLineLevelColumns(selectedViewDetailsRows, setSelectedViewDetailsRows);
    const filtered = fullColumns.filter((column) => column.accessorKey !== '__select_labels' && column.accessorKey !== 'generate_label');
    const hasReason = viewDetailsData.some(row => row?.reason != null && row?.reason !== '');
    if (hasReason) {
      filtered.push(reasonColumn);
    }
    return filtered;
  }, [buildLineLevelColumns, selectedViewDetailsRows, viewDetailsData, reasonColumn]);

  const detailedColumns = useMemo(() => {
    if (!detailedData || detailedData.length === 0) {
      return [];
    }

    const orderedKeys = [
      'line_id',
      'item',
      'po',
      'tracking_number',
      'sscc_codes',
      'bin_number',
      'picked_time',
      'picker_name',
      'ship_confirm_time',
      'movement',
      'moment_time',
      'delivered',
      'delivery_status',
      'delivery_time',
      'trailer_pickup_date'
    ];

    const ordered = orderedKeys;

    const selectionColumn = createSelectionColumn(selectedDetailedRows, setSelectedDetailedRows);
    const generateLabelColumn = {
      accessorKey: 'generate_label',
      header: 'Generate Label',
      minSize: 120,
      size: 140,
      cell: info => {
        const row = info.row?.original || info.getValue();
        return rowHasLabelIdentifier(row) ? (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleGenerateLabel(row);
            }}
            disabled={labelLoading}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Generate Label
          </Button>
        ) : '-';
      }
    };

    const otherColumns = ordered.map(key => {
      const isPickedCountField = key === 'picked' || key === 'is_picked' || key === 'ready_for_picking' || key === 'ready_for_pick' || key === 'ship_confirmed';

      let formattedHeader = key
        .split('_')
        .map(word => {
          const lower = word.toLowerCase();
          if (lower === 'id') return 'ID';
          if (lower === 'sscc') return 'SSCC';
          if (lower === 'po') return 'PO';
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');

      return {
        accessorKey: key,
        header: formattedHeader,
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
        cell: info => {
          let value = info.getValue();
          const row = info.row?.original;
          if (key === 'po' && (value === null || value === undefined || String(value).trim() === '')) {
            value = row?.po ?? row?.po_number ?? row?.ponumber;
          }
          if (value === null || value === undefined || String(value).trim() === '') {
            return '-';
          }
          const displayValue = isPickedCountField ? formatPickedCount(value) : value;
          if (Array.isArray(displayValue)) {
            return displayValue.length > 0 ? displayValue.join(', ') : '-';
          }
          if (typeof displayValue === 'object') {
            return JSON.stringify(displayValue);
          }
          return String(displayValue);
        }
      };
    });
    return [selectionColumn, generateLabelColumn, ...otherColumns];
  }, [createSelectionColumn, detailedData, handleGenerateLabel, labelLoading, rowHasLabelIdentifier]);

  const mainGridActionButtons = useMemo(
    () => (
      <SalesOrderListFilterBar
        key="sales-order-list-filter-bar"
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        shipDateFrom={shipDateFrom}
        shipDateTo={shipDateTo}
        trackingNumber={trackingNumber}
        orderSearch={orderSearch}
        onApply={handleApplyFilters}
        onViewDetails={handleViewDetails}
      />
    ),
    [
      statusFilter,
      setStatusFilter,
      shipDateFrom,
      shipDateTo,
      trackingNumber,
      orderSearch,
      handleApplyFilters,
      handleViewDetails,
    ]
  );

  const salesOrderListMainGrid = useDataGridProTable({
    data: processedData,
    loading: isLoading,
    error: error || null,
    title: 'Sales Order List',
    hideQuickFilter: true,
    columns,
    onDataRefresh: handleRefetch,
    getRowId: (row, index) => getRowId(row, index),
    exportValueTransform,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: handleRowClick,
    muiTableContainerProps: { sx: {} },
    enableCtrlF: !lineLevelDialogOpen && !detailedDialogOpen && !viewDetailsDialogOpen,
    actionButtons: mainGridActionButtons,
  });

  const salesOrderListLineLevelGrid = useDataGridProTable({
    data: lineLevelData || [],
    columns: lineLevelColumns,
    loading: false,
    title: 'sales-order-list-line-level',
    getRowId: (row, index) => getRowId(row, index),
    exportValueTransform,
    initialColumnVisibility: lineLevelColumnVisibility,
    onColumnVisibilityChange: setLineLevelColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: lineLevelAllowNested ? handleLineLevelRowClick : undefined,
    muiTableContainerProps: { sx: {} },
    disablePaper: true,
    error: null,
    enableCtrlF: lineLevelDialogOpen,
  });

  const handleDetailedRowClick = useCallback((row, event) => {
    if (event?.target?.closest('button') || event?.target?.closest('a') || event?.target?.closest('input')) {
      return;
    }
    if (rowHasLabelIdentifier(row)) {
      const rowKey = getRowSelectionKey(row);
      setSelectedDetailedRows((prev) => {
        const next = new Set(prev);
        if (next.has(rowKey)) {
          next.delete(rowKey);
        } else {
          next.add(rowKey);
        }
        return next;
      });
    }
  }, [getRowSelectionKey, rowHasLabelIdentifier]);

  const salesOrderListDetailedGrid = useDataGridProTable({
    data: detailedData || [],
    columns: detailedColumns,
    loading: false,
    title: 'sales-order-list-detailed',
    getRowId: (row, index) => getRowId(row, index),
    exportValueTransform,
    initialColumnVisibility: detailedColumnVisibility,
    onColumnVisibilityChange: setDetailedColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: handleDetailedRowClick,
    muiTableContainerProps: { sx: {} },
    disablePaper: true,
    error: null,
    enableCtrlF: detailedDialogOpen,
  });

  const salesOrderListViewDetailsGrid = useDataGridProTable({
    data: viewDetailsData || [],
    columns: viewDetailsColumns,
    loading: false,
    title: 'sales-order-list-view-details',
    getRowId: (row, index) => getRowId(row, index),
    exportValueTransform,
    initialColumnVisibility: viewDetailsColumnVisibility,
    onColumnVisibilityChange: setViewDetailsColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: handleLineLevelRowClick,
    muiTableContainerProps: { sx: {} },
    disablePaper: true,
    error: null,
    enableCtrlF: viewDetailsDialogOpen,
  });

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {filterError && (
          <Typography color="error" sx={{ px: 2, pt: 1, fontSize: '0.875rem' }}>
            {filterError}
          </Typography>
        )}
        <MuiXDataGridSurface hook={salesOrderListMainGrid} DataGridPro={DataGridPro} />
      </Box>

      {/* FIX 4: keepMounted removed from all dialogs — unmount when closed to free memory */}

      {/* First Level Nested Table Dialog - Line Level Data */}
      <Dialog
        open={lineLevelDialogOpen}
        onClose={() => { setLineLevelDialogOpen(false); setLineLevelAllowNested(true); }}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        PaperProps={{
          sx: {
            width: '96vw',
            maxWidth: '96vw',
            height: '92vh',
            maxHeight: '92vh',
          }
        }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>
            Line Level Data - {selectedTransactionId || 'N/A'}
          </Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => { setLineLevelDialogOpen(false); setLineLevelAllowNested(true); }}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {lineLevelLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : lineLevelError ? (
            <Typography color="error" sx={{ p: 2 }}>
              {lineLevelError}
            </Typography>
          ) : lineLevelData && lineLevelData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: 2, pb: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={lineLevelSelectableRowKeys.length > 0 && selectedLineLevelRows.size === lineLevelSelectableRowKeys.length}
                    indeterminate={selectedLineLevelRows.size > 0 && selectedLineLevelRows.size < lineLevelSelectableRowKeys.length}
                    onChange={(e) => toggleSelectAllRows(setSelectedLineLevelRows, lineLevelData, e.target.checked)}
                    disabled={lineLevelSelectableRowKeys.length === 0}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {selectedLineLevelRows.size > 0 ? `${selectedLineLevelRows.size} selected` : 'Select rows to generate labels'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={labelLoading ? <CircularProgress size={16} /> : <PrintIcon />}
                  onClick={() => handleGenerateSelectedLabels(lineLevelData, selectedLineLevelRows)}
                  disabled={labelLoading || selectedLineLevelRows.size === 0}
                >
                  Generate Labels ({selectedLineLevelRows.size})
                </Button>
              </Box>
              <MuiXDataGridSurface hook={salesOrderListLineLevelGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Typography sx={{ p: 2 }}>No line-level data available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Second Level Nested Table Dialog - Detailed Data */}
      <Dialog
        open={detailedDialogOpen}
        onClose={() => setDetailedDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        PaperProps={{
          sx: {
            width: '96vw',
            maxWidth: '96vw',
            height: '92vh',
            maxHeight: '92vh',
          }
        }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>Detailed Data</Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => setDetailedDialogOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {detailedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : detailedData && detailedData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: 2, pb: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={detailedSelectableRowKeys.length > 0 && selectedDetailedRows.size === detailedSelectableRowKeys.length}
                    indeterminate={selectedDetailedRows.size > 0 && selectedDetailedRows.size < detailedSelectableRowKeys.length}
                    onChange={(e) => toggleSelectAllRows(setSelectedDetailedRows, detailedData, e.target.checked)}
                    disabled={detailedSelectableRowKeys.length === 0}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {selectedDetailedRows.size > 0 ? `${selectedDetailedRows.size} selected` : 'Select rows to generate labels'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={labelLoading ? <CircularProgress size={16} /> : <PrintIcon />}
                  onClick={() => handleGenerateSelectedLabels(detailedData, selectedDetailedRows)}
                  disabled={labelLoading || selectedDetailedRows.size === 0}
                >
                  Generate Labels ({selectedDetailedRows.size})
                </Button>
              </Box>
              <MuiXDataGridSurface hook={salesOrderListDetailedGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Typography sx={{ p: 2 }}>No detailed data available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDetailsDialogOpen}
        onClose={() => setViewDetailsDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        PaperProps={{
          sx: {
            width: '96vw',
            maxWidth: '96vw',
            height: '92vh',
            maxHeight: '92vh',
          }
        }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>
            View Details - {statusFilter === 'closed' ? 'Closed Orders' : 'Open Orders'} Summary
          </Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => setViewDetailsDialogOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {viewDetailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : viewDetailsError ? (
            <Typography color="error" sx={{ p: 2 }}>
              {viewDetailsError}
            </Typography>
          ) : viewDetailsData && viewDetailsData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={salesOrderListViewDetailsGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Typography sx={{ p: 2 }}>No line-level data available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={labelPreviewOpen}
        onClose={closeLabelPreviewDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { width: '90%', maxWidth: '1200px', maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.1rem', fontWeight: 500, lineHeight: 1.6 }}>
            {labelPreviewMode === 'pu' ? 'Label Preview' : 'Shipping Label Preview'}
            {labelPreviewItems.length > 1 ? ` (${labelPreviewItems.length})` : ''}
          </Typography>
          {labelPreviewMode === 'pu' && (
            <IconButton
              aria-label="download pdf"
              size="small"
              onClick={() => {
                if (allZplLabels.length > 0) {
                  downloadCombinedLtlPdf(allZplLabels, `labels_${Date.now()}.pdf`);
                }
              }}
              disabled={allZplLabels.length === 0 || pdfDownloading || labelLoading}
              sx={{ position: 'absolute', right: 48, top: 8 }}
            >
              <DownloadIcon />
            </IconButton>
          )}
          <IconButton
            aria-label="close"
            size="small"
            onClick={closeLabelPreviewDialog}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, p: 2 }}>
          {labelLoading || pdfDownloading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 1.5 }}>
                {pdfDownloading ? 'Downloading labels, please wait...' : 'Generating labels...'}
              </Typography>
            </Box>
          ) : (
            <>
              {labelError && (
                <Typography color="error" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                  {labelError}
                </Typography>
              )}
              {labelPreviewItems.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, overflowY: 'auto' }}>
                  {labelPreviewMode === 'pu'
                    ? labelPreviewItems.map((item, index) => (
                      <Box
                        key={`${item.ssccCode || index}`}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                      >
                        {item.ssccCode && (
                          <Typography sx={{ fontWeight: 600, mb: 1 }}>SSCC: {item.ssccCode}</Typography>
                        )}
                        {item.imageUrl && (
                          <img
                            src={item.imageUrl}
                            alt={`Label ${item.ssccCode || index}`}
                            style={{ maxWidth: 400, border: '1px solid #ccc' }}
                          />
                        )}
                      </Box>
                    ))
                    : labelPreviewItems.map((item, index) => (
                      <Box key={`${item.trackingNumber}_${index}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                        <Typography sx={{ fontWeight: 600, mb: 2 }}>
                          Tracking Number: {item.trackingNumber || 'N/A'}
                        </Typography>
                        {item.labelImageUrl && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                            <Typography sx={{ fontWeight: 500, mb: 1 }}>Shipping Label</Typography>
                            <img
                              src={item.labelImageUrl}
                              alt="Shipping Label"
                              style={{ maxWidth: 400, border: '1px solid #ccc', marginBottom: 8, transform: 'rotate(180deg)', transformOrigin: 'center center' }}
                            />
                            <Button variant="contained" href={item.labelImageUrl} download={`shipping_label_${item.trackingNumber || index}.png`} sx={{ mt: 1 }}>
                              Download Label
                            </Button>
                          </Box>
                        )}
                        {item.customLabelImageUrl && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                            <Typography sx={{ fontWeight: 500, mb: 1 }}>{item.customLabelSource || 'Custom Label'}</Typography>
                            <img
                              src={item.customLabelImageUrl}
                              alt="Custom Shipping Label"
                              style={{ maxWidth: 400, border: '1px solid #ccc', marginBottom: 8 }}
                            />
                            <Button variant="contained" href={item.customLabelImageUrl} download={`${(item.customLabelSource || 'custom').replace(/\s+/g, '_')}_${item.trackingNumber || index}.png`}>
                              Download Label
                            </Button>
                          </Box>
                        )}
                        {item.documents?.length > 0 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, width: '100%' }}>
                            <Typography sx={{ fontWeight: 500, mb: 1 }}>Documents</Typography>
                            {item.documents.map((doc, docIndex) => (
                              <Box key={`${item.trackingNumber}_${doc.name}_${docIndex}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mb: 2 }}>
                                {doc.mimeType.startsWith('image/') ? (
                                  <img src={doc.url} alt={`Document ${docIndex + 1}`} style={{ maxWidth: 400, border: '1px solid #ccc', marginBottom: 8 }} />
                                ) : doc.mimeType === 'application/pdf' ? (
                                  <iframe src={doc.url} title={`Document ${docIndex + 1}`} style={{ width: '100%', maxWidth: 460, height: 420, border: '1px solid #ccc', marginBottom: 8 }} />
                                ) : (
                                  <Typography sx={{ mb: 1 }}>Preview not available for this document type.</Typography>
                                )}
                                <Button variant="contained" href={doc.url} download={doc.name}>
                                  Download Document {docIndex + 1}
                                </Button>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                </Box>
              ) : !labelError ? (
                <Typography sx={{ p: 2 }}>No label preview available.</Typography>
              ) : null}
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SalesOrderList;